import { constants, Dropdown, Icon, Input, Select } from "@findemdev/maui";
import { Fragment, useCallback, useState, type ChangeEvent } from "react";
import {
  ALL_ASSIGNEES_FILTER_VALUE,
  PROJECT_TOOLBAR_CONTROL_PLACEMENT,
} from "../../constants";
import {
  CreateButton,
  FilterField,
  FilterFieldLabel,
  IconButton,
  ImpersonationField,
  MenuItemContent,
  OrgField,
  SearchField,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
} from "../../RaasClientPortal.styled";
import type {
  IFilterOption,
  ProjectToolbarControlKey,
  StatusFilter,
} from "../../types";
import { useRaasPortalIdentity } from "../../hooks/useRaasPortalIdentity";
import { ResourceFileUploadModal } from "../ResourceFileUpload";

const { colors } = constants;

interface IProjectToolbarProps {
  assignedToFilter: {
    isLoading: boolean;
    onChange: ({ value }: { value: string }) => void;
    options: IFilterOption[];
    value: string;
  };
  clientFilter: string;
  clientFilterOptions: IFilterOption[];
  controlKeys: readonly ProjectToolbarControlKey[];
  impersonationSelector: {
    isLoading: boolean;
    onUserChange: ({ value }: { value?: string }) => void;
    selectedUserId?: string;
    userOptions: IFilterOption[];
    visible: boolean;
  };
  orgSelector: {
    allowClear: boolean;
    isLoading: boolean;
    onOrgChange: ({ value }: { value?: string }) => void;
    orgOptions: IFilterOption[];
    selectedAccountId?: string;
    visible: boolean;
  };
  onCreateProjectClick: () => void;
  onClientFilterChange: ({ value }: { value: string }) => void;
  onSearchTermChange: ({ value }: { value: string }) => void;
  onStatusFilterChange: ({ value }: { value: StatusFilter }) => void;
  searchTerm: string;
  statusFilter: StatusFilter;
  statusFilterOptions: Array<{ label: string; value: StatusFilter }>;
}

export const ProjectToolbar = ({
  assignedToFilter,
  clientFilter,
  clientFilterOptions,
  controlKeys,
  impersonationSelector,
  orgSelector,
  onCreateProjectClick,
  onClientFilterChange,
  onSearchTermChange,
  onStatusFilterChange,
  searchTerm,
  statusFilter,
  statusFilterOptions,
}: IProjectToolbarProps) => {
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const { joid } = useRaasPortalIdentity();

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    onSearchTermChange({ value: event.target.value });
  }, [onSearchTermChange]);

  const handleOpenUploadModal = useCallback(() => {
    setIsUploadModalVisible(true);
  }, []);

  const handleCloseUploadModal = useCallback(() => {
    setIsUploadModalVisible(false);
  }, []);
  const leftControlKeys = controlKeys.filter(
    (controlKey) => PROJECT_TOOLBAR_CONTROL_PLACEMENT[controlKey] === "left",
  );
  const rightControlKeys = controlKeys.filter(
    (controlKey) => PROJECT_TOOLBAR_CONTROL_PLACEMENT[controlKey] === "right",
  );

  const renderControl = (controlKey: ProjectToolbarControlKey) => {
    switch (controlKey) {
      case "search":
        return (
          <SearchField>
            <Input
              allowClear={true}
              debounceDelay={0}
              onChange={handleSearchChange}
              placeholder="Search projects"
              prefix={
                <Icon icon="search" $size={18} $color={colors["grey-500"]} />
              }
              value={searchTerm}
            />
          </SearchField>
        );
      case "status_filter":
        return (
          <FilterField $pill>
            <Select
              onChange={(value) =>
                onStatusFilterChange({ value: value as StatusFilter })
              }
              options={statusFilterOptions}
              suffixIcon={<Icon icon="arrow_drop_down" $size={22} />}
              value={statusFilter}
            />
          </FilterField>
        );
      case "client_filter":
        return (
          <FilterField>
            <FilterFieldLabel htmlFor="raas-client-filter">
              Client
            </FilterFieldLabel>
            <Select
              id="raas-client-filter"
              onChange={(value) =>
                onClientFilterChange({ value: String(value) })
              }
              options={clientFilterOptions}
              suffixIcon={<Icon icon="arrow_drop_down" $size={22} />}
              value={clientFilter}
            />
          </FilterField>
        );
      case "assigned_to_filter":
        return (
          <FilterField>
            <FilterFieldLabel htmlFor="raas-assigned-to-filter">
              Assigned to
            </FilterFieldLabel>
            <Select
              // Only offer the clear icon when there is a filter to clear;
              // "All Assignees" is the empty state, not a selection.
              allowClear={
                assignedToFilter.value !== ALL_ASSIGNEES_FILTER_VALUE
              }
              filterOption={(input, option) =>
                String(option?.label || "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              loading={assignedToFilter.isLoading}
              id="raas-assigned-to-filter"
              onChange={(value) =>
                assignedToFilter.onChange({
                  value: value ? String(value) : ALL_ASSIGNEES_FILTER_VALUE,
                })
              }
              options={assignedToFilter.options}
              showSearch
              suffixIcon={<Icon icon="arrow_drop_down" $size={22} />}
              value={assignedToFilter.value}
            />
          </FilterField>
        );
      case "org_selector":
        if (!orgSelector.visible) return null;

        return (
          <OrgField>
            <FilterFieldLabel htmlFor="raas-org-selector">
              Organization
            </FilterFieldLabel>
            <Select
              allowClear={orgSelector.allowClear}
              filterOption={(input, option) =>
                String(option?.label || "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              loading={orgSelector.isLoading}
              id="raas-org-selector"
              onChange={(value) =>
                orgSelector.onOrgChange({
                  value: value ? String(value) : undefined,
                })
              }
              options={orgSelector.orgOptions}
              placeholder="Switch org"
              showSearch
              suffixIcon={<Icon icon="arrow_drop_down" $size={22} />}
              value={orgSelector.selectedAccountId}
            />
          </OrgField>
        );
      case "impersonation_selector":
        if (!impersonationSelector.visible) return null;

        return (
          <ImpersonationField>
            <FilterFieldLabel htmlFor="raas-user-selector">
              User
            </FilterFieldLabel>
            <Select
              allowClear={true}
              filterOption={(input, option) =>
                String(option?.label || "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              loading={impersonationSelector.isLoading}
              id="raas-user-selector"
              onChange={(value) =>
                impersonationSelector.onUserChange({
                  value: value ? String(value) : undefined,
                })
              }
              options={impersonationSelector.userOptions}
              placeholder="Switch user"
              showSearch
              suffixIcon={<Icon icon="arrow_drop_down" $size={22} />}
              value={impersonationSelector.selectedUserId}
            />
          </ImpersonationField>
        );
      case "create_project":
        return (
          <CreateButton type="primary" htmlType="button" onClick={onCreateProjectClick}>
            <Icon icon="add" $size={18} />
            Create Project
          </CreateButton>
        );
      case "upload_template":
        return (
          <>
            <Dropdown
              menu={{
                items: [
                  {
                    key: "template_config",
                    label: (
                      <MenuItemContent>
                        <Icon icon="description" $size={18} />
                        Template Config
                      </MenuItemContent>
                    ),
                  },
                ],
                onClick: ({ key }) => {
                  if (key === "template_config") {
                    handleOpenUploadModal();
                  }
                },
              }}
              trigger={["click"]}
            >
              <IconButton
                aria-label="Template options"
                type="button"
              >
                <Icon icon="more_vert" $size={18} />
              </IconButton>
            </Dropdown>
            <ResourceFileUploadModal
              category="exec_project"
              joid={joid}
              onClose={handleCloseUploadModal}
              visible={isUploadModalVisible}
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Toolbar>
      <ToolbarLeft>
        {leftControlKeys.map((controlKey) => (
          <Fragment key={controlKey}>{renderControl(controlKey)}</Fragment>
        ))}
      </ToolbarLeft>
      <ToolbarRight>
        {rightControlKeys.map((controlKey) => (
          <Fragment key={controlKey}>{renderControl(controlKey)}</Fragment>
        ))}
      </ToolbarRight>
    </Toolbar>
  );
};
